import React, { useEffect, useMemo, useRef, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  RSelect,
  ReadableCell,
  Row,
  TooltipComponent,
} from "../../../components/Component";
import { Form, FormGroup, Modal, ModalBody, ModalHeader } from "reactstrap";
import AdminFormModal from "../../../components/admin/AdminFormModal";
import moment from "moment";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import { getPublicUrl } from "../../../utils/Utils";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import {
  ISO_3166_ALPHA2_OPTIONS,
  mergeCountryFilterOptions,
  resolveIsoCountrySelectValue,
} from "../../../utils/iso3166Alpha2Options";
import ConfirmModal from "../../../components/partials/user-manage/ConfirmModal";
import BeforeAfterEditor from "../../../components/partials/user-manage/BeforeAfterEditor";
import MediaSelector from "../../../components/partials/user-manage/MediaSelector";

/** Remove duplicate before/after cases (same image URL or same title+url). */
function dedupeBeforeAfterCases(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  const out = [];
  arr.forEach((c, i) => {
    const url = String(c?.imageUrl ?? "").trim().toLowerCase();
    const title = String(c?.title ?? "").trim().toLowerCase();
    const key = url ? `url:${url}` : `title:${title}:idx:${i}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      ...c,
      title: String(c?.title ?? "").trim(),
      imageUrl: String(c?.imageUrl ?? "").trim(),
      order: typeof c?.order === "number" && !Number.isNaN(c.order) && c.order >= 0 ? Math.floor(c.order) : out.length,
    });
  });
  return out.map((c, i) => ({ ...c, order: i }));
}

/** Hint for API; server assigns a unique `landingSlug` if missing or conflicting. */
function landingSlugCandidateFromClinicName(name) {
  if (name == null || typeof name !== "string") return null;
  const s = name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return s || null;
}

const ClinicManagement = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [clinics, setClinics] = useState([]);
  const [partners, setPartners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [tablesm, updateTableSm] = useState(false);
  const [onSearch, setOnSearch] = useState(true);
  const [onSearchText, setOnSearchText] = useState("");
  const [modal, setModal] = useState({ edit: false, add: false });
  const [editClinic, setEditClinic] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingIntroVideo, setUploadingIntroVideo] = useState(false);
  const [uploadingTestimonialVideos, setUploadingTestimonialVideos] = useState(false);
  const [introVideoMode, setIntroVideoMode] = useState("upload"); // "upload" | "url"
  const [introVideoStagedFile, setIntroVideoStagedFile] = useState(null);
  const [testimonialDragOver, setTestimonialDragOver] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [pickerMode, setPickerMode] = useState("photos"); // 'photos' | 'beforeAfter' | 'banner' | 'testimonials'
  const [showCopyFromClinic, setShowCopyFromClinic] = useState(false);
  const [copyFromClinicId, setCopyFromClinicId] = useState("");
  const [copyFromClinicTypes, setCopyFromClinicTypes] = useState({
    banner: false,
    photos: false,
    beforeAfter: false,
    testimonials: false,
  });
  const [copyFromClinicLoading, setCopyFromClinicLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    partnerId: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      zipCode: ""
    },
    phoneNumber: "",
    email: "",
    website: "",
    defaultCurrency: "USD",
    availability: true,
    isDefault: false,
    status: "draft"
  });
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const { register, handleSubmit, errors, reset, setValue } = useForm();

  // Status values aligned with API: active, inactive, draft, pending
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "draft", label: "Draft" },
    { value: "pending", label: "Pending" },
  ];
  const [filters, setFilters] = useState({ status: "", country: "", date: "", partner: "" });
  const location = useLocation();
  const openClinicHandledRef = useRef(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState({
    isOpen: false,
    type: null, // "approveClinic" | "rejectClinic" | "deleteClinic" | "suspendClinic"
    clinic: null,
    onConfirm: null,
  });
  const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);
  const [initialAddFormSnapshot, setInitialAddFormSnapshot] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [discardTarget, setDiscardTarget] = useState(null); // "edit" | "add"
  /** Local-time preview for IANA timezone (same idea as partner TC wizard). */
  const [adminTimezonePreviewText, setAdminTimezonePreviewText] = useState(null);

  const getEditFormSnapshot = (clinic, existing = [], newCount = 0) => {
    if (!clinic) return null;
    const addr = clinic.address || {};
    return {
      name: (clinic.name || "").trim(),
      status: clinic.status || "draft",
      address: {
        street: (addr.street || addr.address || "").trim(),
        city: (addr.city || "").trim(),
        state: (addr.state || "").trim(),
        country: (addr.country || "").trim(),
        zipCode: (addr.zipCode || "").trim(),
      },
      phoneNumber: (clinic.phoneNumber || "").trim(),
      email: (clinic.email || "").trim(),
      website: (clinic.website || "").trim(),
      defaultCurrency: clinic.defaultCurrency || "USD",
      availability: clinic.availability !== false,
      isDefault: !!clinic.isDefault,
      landingSlug: clinic.landingSlug != null ? String(clinic.landingSlug).trim() : null,
      formOptionsRootCategoryId: clinic.formOptionsRootCategoryId != null ? String(clinic.formOptionsRootCategoryId).trim() : null,
      introVideoUrl: (clinic.introVideoUrl || "").trim(),
      testimonialVideos: JSON.stringify(Array.isArray(clinic.testimonialVideos) ? clinic.testimonialVideos : []),
      beforeAfterCases: JSON.stringify(Array.isArray(clinic.beforeAfterCases) ? clinic.beforeAfterCases : []),
      existingPhotos: JSON.stringify(existing),
      newPhotoFilesCount: newCount,
      consultationDurationMinutes:
        clinic.consultationDurationMinutes != null && clinic.consultationDurationMinutes !== ""
          ? Number(clinic.consultationDurationMinutes)
          : 20,
      tcBufferMinutes:
        clinic.tcBufferMinutes === null || clinic.tcBufferMinutes === undefined || clinic.tcBufferMinutes === ""
          ? ""
          : Number(clinic.tcBufferMinutes),
      tcMinBookingDelayHours:
        clinic.tcMinBookingDelayHours === null || clinic.tcMinBookingDelayHours === undefined || clinic.tcMinBookingDelayHours === ""
          ? ""
          : Number(clinic.tcMinBookingDelayHours),
      tcMaxBookingDays:
        clinic.tcMaxBookingDays === null || clinic.tcMaxBookingDays === undefined || clinic.tcMaxBookingDays === ""
          ? ""
          : Number(clinic.tcMaxBookingDays),
      tcSlotHorizonDays:
        clinic.tcSlotHorizonDays === null || clinic.tcSlotHorizonDays === undefined || clinic.tcSlotHorizonDays === ""
          ? ""
          : Number(clinic.tcSlotHorizonDays),
    };
  };

  const isEditFormDirty = () => {
    if (!initialEditSnapshot || !editClinic) return false;
    const current = getEditFormSnapshot(editClinic, existingPhotos, newPhotoFiles.length);
    return JSON.stringify(current) !== JSON.stringify(initialEditSnapshot);
  };

  const isAddFormDirty = () => {
    if (!initialAddFormSnapshot) return false;
    const current = JSON.stringify({
      ...formData,
      address: formData.address || {},
    });
    return current !== JSON.stringify({
      ...initialAddFormSnapshot,
      address: initialAddFormSnapshot.address || {},
    });
  };

  const exitEditModal = () => {
    if (isEditFormDirty()) {
      setShowDiscardConfirm(true);
      setDiscardTarget("edit");
    } else {
      setModal({ edit: false });
      setEditClinic(null);
      setExistingPhotos([]);
      setNewPhotoFiles([]);
      setInitialEditSnapshot(null);
    }
  };

  const exitAddModal = () => {
    if (isAddFormDirty()) {
      setShowDiscardConfirm(true);
      setDiscardTarget("add");
    } else {
      setModal({ add: false });
      resetForm();
      setInitialAddFormSnapshot(null);
    }
  };

  const confirmDiscard = () => {
    if (discardTarget === "edit") {
      setModal({ edit: false });
      setEditClinic(null);
      setExistingPhotos([]);
      setNewPhotoFiles([]);
      setInitialEditSnapshot(null);
    } else if (discardTarget === "add") {
      setModal({ add: false });
      resetForm();
      setInitialAddFormSnapshot(null);
    }
    setShowDiscardConfirm(false);
    setDiscardTarget(null);
  };

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/category/public", { signal: ac.signal })
      .then((res) => {
        const list = res.data?.data ?? [];
        setCategoryOptions(
          Array.isArray(list)
            ? list.map((c) => ({ value: c._id, label: c.label || `Category ${c.uid || c._id}` }))
            : []
        );
      })
      .catch((e) => {
        if (!isAbortError(e)) setCategoryOptions([]);
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/medical-partners", { signal: ac.signal })
      .then((res) => {
        const partnersList = res.data?.data || res.data || [];
        setPartners(partnersList.filter((p) => p.status === "active" || p.status === "Active"));
      })
      .catch((e) => {
        if (!isAbortError(e)) console.error("Failed to load partners", e);
      });
    return () => ac.abort();
  }, []);

  // Pre-fill Partner / status filters from URL (e.g. from Medical partners stats card → pending clinics)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const partnerIdFromUrl = params.get("partnerId");
    const statusFromUrl = params.get("status");
    setFilters((prev) => {
      let next = { ...prev };
      if (partnerIdFromUrl && partnerIdFromUrl.trim()) {
        next = { ...next, partner: partnerIdFromUrl.trim() };
      }
      const allowed = new Set(["active", "inactive", "draft", "pending"]);
      if (statusFromUrl && allowed.has(String(statusFromUrl).trim().toLowerCase())) {
        next = { ...next, status: String(statusFromUrl).trim().toLowerCase() };
      }
      return next;
    });
  }, [location.search]);

  useEffect(() => {
    const tz = editClinic?.timezone && String(editClinic.timezone).trim();
    if (!tz) {
      setAdminTimezonePreviewText(null);
      return;
    }
    try {
      const fmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      setAdminTimezonePreviewText(fmt.format(new Date()));
    } catch {
      setAdminTimezonePreviewText(null);
    }
  }, [editClinic?.timezone]);

  const fetchClinics = React.useCallback((options = {}) => {
    const { signal } = options;
    if (!token) {
      setClinics([]);
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    setError("");
    http
      .get("/api/v1/partners/medical/clinics", { signal })
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        const clinicsList = Array.isArray(list) ? list : [];
        setClinics(clinicsList);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        console.error("Failed to load clinics", e);
        setError(e.response?.data?.message || "Failed to load clinics");
        setClinics([]);
      })
      .finally(() => {
        if (!signal?.aborted) {
          setLoading(false);
          setInitialLoading(false);
        }
      });
  }, [token]);

  useEffect(() => {
    const ac = new AbortController();
    fetchClinics({ signal: ac.signal });
    return () => ac.abort();
  }, [fetchClinics]);

  const countries = useMemo(() => {
    if (!Array.isArray(clinics)) return [...ISO_3166_ALPHA2_OPTIONS];
    const legacy = [...new Set(clinics.map((c) => c?.address?.country).filter(Boolean))];
    return mergeCountryFilterOptions(legacy);
  }, [clinics]);

  const debouncedClinicSearch = useDebouncedValue(onSearchText, 300);

  const filteredClinics = useMemo(() => {
    if (!Array.isArray(clinics)) return [];
    let data = [...clinics];
    if (debouncedClinicSearch) {
      const q = debouncedClinicSearch.toLowerCase();
      data = data.filter((c) => {
        const fields = [c?.name, c?.partner?.organization_name, c?.address?.city, c?.address?.country, c?.partner?.contact_person_email];
        return fields.some((f) => f && String(f).toLowerCase().includes(q));
      });
    }
    if (filters.status) data = data.filter((c) => c?.status === filters.status);
    if (filters.country) data = data.filter((c) => c?.address?.country === filters.country);
    if (filters.partner) data = data.filter((c) => c?.partner?._id === filters.partner || c?.partner?.toString() === filters.partner);
    if (filters.date) data = data.filter((c) => moment(c?.createdAt).format("YYYY-MM-DD") === filters.date);
    return data;
  }, [clinics, debouncedClinicSearch, filters]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = Array.isArray(filteredClinics) ? filteredClinics.slice(indexOfFirstItem, indexOfLastItem) : [];

  const toggleSearch = () => setOnSearch(!onSearch);

  const onFilterChange = (e, type) => {
    if (!type) {
      setOnSearchText(e.target.value);
      return;
    }
    const value = type === "date" ? e.target.value : e?.value || "";
    setFilters((prev) => ({ ...prev, [type]: value }));
  };

  const applyFilter = () => updateTableSm(false);
  const resetFilter = () => {
    setFilters({ status: "", country: "", date: "", partner: "" });
    setOnSearchText("");
    updateTableSm(false);
  };

  const onEditClick = (clinic) => {
    const rawCases = Array.isArray(clinic?.beforeAfterCases) ? clinic.beforeAfterCases : [];
    const beforeAfterCases = dedupeBeforeAfterCases(rawCases);
    setEditClinic({
      ...clinic,
      introVideoUrl: clinic?.introVideoUrl || "",
      testimonialVideos: Array.isArray(clinic?.testimonialVideos)
        ? clinic.testimonialVideos.map((v) => ({ title: v?.title || "", url: v?.url || "", thumbnail: v?.thumbnail || null }))
        : [],
      beforeAfterCases
    });
    const photos = clinic?.photos || [];
    const photosArray = Array.isArray(photos)
      ? photos.map((photo) => (typeof photo === "string" ? photo : photo.url || photo.path || photo))
      : [];
    setExistingPhotos(photosArray);
    setNewPhotoFiles([]);
    const snapshotData = {
      ...clinic,
      introVideoUrl: clinic?.introVideoUrl || "",
      testimonialVideos: Array.isArray(clinic?.testimonialVideos)
        ? clinic.testimonialVideos.map((v) => ({ title: v?.title || "", url: v?.url || "", thumbnail: v?.thumbnail || null }))
        : [],
      beforeAfterCases,
      address: clinic?.address || {},
    };
    setInitialEditSnapshot(getEditFormSnapshot(snapshotData, photosArray, 0));
    setModal({ edit: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("openClinic")) {
      openClinicHandledRef.current = false;
    }
  }, [location.search]);

  // Deep link: /clinics?partnerId=…&openClinic=<clinicId> opens the edit modal (from Medical partner detail)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get("openClinic")?.trim();
    if (!openId || !Array.isArray(clinics) || clinics.length === 0 || openClinicHandledRef.current) {
      return;
    }
    const found = clinics.find((c) => String(c._id) === openId);
    if (!found) {
      return;
    }
    openClinicHandledRef.current = true;
    onEditClick(found);
    params.delete("openClinic");
    const qs = params.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    // onEditClick is stable enough for this flow; including it would re-run every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinics, location.search, location.pathname, history]);

  const onSaveEdit = (data) => {
    if (!editClinic) return;
    if (!(editClinic?.address?.country || "").trim()) {
      swal("Validation", "Country is required.", "warning");
      return;
    }
    setLoading(true);
    const id = editClinic?._id || editClinic?.id;

    const base = { ...editClinic };
    if (base.partner && typeof base.partner === "object" && base.partner._id != null) {
      base.partner = base.partner._id;
    }
    if (Array.isArray(base.categories)) {
      base.categories = base.categories.map((c) => (c && typeof c === "object" && c._id != null ? c._id : c));
    }
    if (Array.isArray(base.clinicProducts)) {
      base.clinicProducts = base.clinicProducts.map((p) => ({
        ...p,
        category:
          p.category && typeof p.category === "object" && p.category._id != null ? p.category._id : p.category,
      }));
    }
    delete base.__v;
    delete base.createdAt;
    delete base.updatedAt;

    const updateData = {
      ...base,
      name: data.name ?? editClinic.name,
      address: {
        street: data.address_street ?? editClinic.address?.street ?? "",
        city: data.address_city ?? editClinic.address?.city ?? "",
        state: data.address_state ?? editClinic.address?.state ?? "",
        country: (editClinic.address?.country || data.address_country || "").trim(),
        zipCode: data.address_zipCode ?? editClinic.address?.zipCode ?? "",
      },
      phoneNumber: data.phoneNumber ?? editClinic.phoneNumber ?? "",
      email: data.email ?? editClinic.email ?? "",
      website: data.website ?? editClinic.website ?? "",
      defaultCurrency: (data.defaultCurrency ?? editClinic.defaultCurrency) || "USD",
      availability: editClinic.availability,
      status: editClinic.status || "draft",
      offersTeleconsultation: editClinic.offersTeleconsultation === true,
      offersMedicalTourism: editClinic.offersMedicalTourism !== false,
      timezone: (editClinic.timezone && String(editClinic.timezone).trim()) || null,
      jurisdiction: (editClinic.jurisdiction && String(editClinic.jurisdiction).trim()) || undefined,
      emergencyContact: (editClinic.emergencyContact && String(editClinic.emergencyContact).trim()) || undefined,
      insuranceStatus: (editClinic.insuranceStatus && String(editClinic.insuranceStatus).trim()) || undefined,
      consultationDurationMinutes: Math.min(
        60,
        Math.max(10, Number(editClinic.consultationDurationMinutes) || 20)
      ),
      tcBufferMinutes:
        editClinic.tcBufferMinutes === "" || editClinic.tcBufferMinutes == null
          ? null
          : Math.min(180, Math.max(0, Number(editClinic.tcBufferMinutes))),
      tcMinBookingDelayHours:
        editClinic.tcMinBookingDelayHours === "" || editClinic.tcMinBookingDelayHours == null
          ? null
          : Math.min(168, Math.max(0, Number(editClinic.tcMinBookingDelayHours))),
      tcMaxBookingDays:
        editClinic.tcMaxBookingDays === "" || editClinic.tcMaxBookingDays == null
          ? null
          : Math.min(120, Math.max(1, Number(editClinic.tcMaxBookingDays))),
      tcSlotHorizonDays:
        editClinic.tcSlotHorizonDays === "" || editClinic.tcSlotHorizonDays == null
          ? null
          : Math.min(120, Math.max(1, Number(editClinic.tcSlotHorizonDays))),
      landingSlug: editClinic.landingSlug != null ? String(editClinic.landingSlug).trim() || null : null,
      formOptionsRootCategoryId: (editClinic.formOptionsRootCategoryId && String(editClinic.formOptionsRootCategoryId).trim()) || null,
      introVideoUrl: editClinic.introVideoUrl ? String(editClinic.introVideoUrl).trim() || null : null,
      testimonialVideos: (Array.isArray(editClinic.testimonialVideos) ? editClinic.testimonialVideos : [])
        .filter((v) => v && v.url && String(v.url).trim())
        .map((v) => ({ title: String(v.title || "").trim(), url: String(v.url).trim(), thumbnail: v.thumbnail ? String(v.thumbnail).trim() : null })),
      beforeAfterCases: dedupeBeforeAfterCases(Array.isArray(editClinic.beforeAfterCases) ? editClinic.beforeAfterCases : []),
    };
    
    http
      .put(`/api/v1/partners/medical/clinics/${id}`, updateData, { suppressInlineErrorSwal: true })
      .then(async (res) => {
        if (res.data?.success) {
          // Upload new photos if any
          if (newPhotoFiles.length > 0) {
            setUploadingPhotos(true);
            try {
              // Upload photos one by one (backend expects 'file' field, not 'photos')
              for (const photoFile of newPhotoFiles) {
                const formDataPhoto = new FormData();
                formDataPhoto.append('files', photoFile);
              
                await http.post(
                  `/api/v1/partners/medical/clinics/${id}/photos`,
                  formDataPhoto,
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                      Authorization: `Bearer ${token}`,
                    },
                    suppressInlineErrorSwal: true,
                  }
                );
              }
              
              setNewPhotoFiles([]);
              // Refresh clinic data to get updated photos
              const updatedClinicResponse = await http.get(`/api/v1/partners/medical/clinics/${id}`, {
                suppressInlineErrorSwal: true,
              });
              const updatedClinic = updatedClinicResponse.data?.data || updatedClinicResponse.data;
              if (updatedClinic?.photos && Array.isArray(updatedClinic.photos)) {
                setExistingPhotos(updatedClinic.photos.map(photo => typeof photo === 'string' ? photo : photo.url || photo.path || photo));
              }
            } catch (photoError) {
              console.error("Error uploading photos:", photoError);
              swal(
                "Photo upload issue",
                formatAdminApiError(photoError, "One or more photos could not be uploaded"),
                "warning"
              );
            } finally {
              setUploadingPhotos(false);
            }
          }
          
          swal("Success", "Clinic updated successfully", "success");
          fetchClinics();
          setModal({ edit: false });
          setEditClinic(null);
          setExistingPhotos([]);
          setNewPhotoFiles([]);
        } else {
          throw new Error(res.data?.message || "Update failed");
        }
      })
      .catch((e) => {
        swal("Clinic could not be saved", formatAdminApiError(e, "Update error"), "error");
      })
      .finally(() => {
        setLoading(false);
        setUploadingPhotos(false);
      });
  };

  const onSuspend = (clinic) => {
    setShowConfirmModal({
      isOpen: true,
      type: "suspendClinic",
      clinic,
      onConfirm: () => {
        suspendClinic(clinic);
        setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null });
      },
    });
  };

  const suspendClinic = (clinic) => {
    setLoading(true);
    const id = clinic?._id || clinic?.id;
    http
      .put(`/api/v1/partners/medical/clinics/${id}`, { ...clinic, status: "inactive" }, {
        suppressInlineErrorSwal: true,
      })
      .then((res) => {
        if (res.data?.success) {
          swal("Success", "Clinic suspended successfully", "success");
          fetchClinics();
        } else {
          throw new Error(res.data?.message || "Suspend failed");
        }
      })
      .catch((e) => {
        swal("Clinic could not be suspended", formatAdminApiError(e, "Error suspending clinic"), "error");
      })
      .finally(() => setLoading(false));
  };

  const onDelete = (clinic) => {
    setShowConfirmModal({
      isOpen: true,
      type: "deleteClinic",
      clinic,
      onConfirm: () => {
        deleteClinic(clinic);
        setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null });
      },
    });
  };

  const deleteClinic = (clinic) => {
    setLoading(true);
    const id = clinic?._id || clinic?.id;
    http
      .delete(`/api/v1/partners/medical/clinics/${id}`, { suppressInlineErrorSwal: true })
      .then((res) => {
        if (res.data?.success) {
          swal("Success", "Clinic deleted successfully", "success");
          fetchClinics();
        } else {
          throw new Error(res.data?.message || "Delete failed");
        }
      })
      .catch((e) => {
        swal("Clinic could not be deleted", formatAdminApiError(e, "Error deleting clinic"), "error");
      })
      .finally(() => setLoading(false));
  };

  const updateClinicStatus = async (clinicId, status, reason = "") => {
    try {
      setLoading(true);
      await http.put(
        `/api/v1/partners/medical/clinics/${clinicId}`,
        {
          status,
          rejectionReason: reason || undefined,
        },
        { suppressInlineErrorSwal: true }
      );

      swal("Success", `Clinic ${status === "active" ? "approved" : "rejected"}`, "success");
      fetchClinics();
    } catch (error) {
      console.error("Error updating clinic:", error);
      swal("Clinic status could not be updated", formatAdminApiError(error, "Update failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmUpdateClinic = (clinic, status) => {
    setShowConfirmModal({
      isOpen: true,
      type: status === "active" ? "approveClinic" : "rejectClinic",
      clinic,
      onConfirm: () => {
        updateClinicStatus(clinic._id || clinic.id, status);
        setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null });
      },
    });
  };

  const onCreateSubmit = (data) => {
    if (!(formData.address?.country || "").trim()) {
      swal("Validation", "Country is required.", "warning");
      return;
    }
    setLoading(true);
    const slugHint = landingSlugCandidateFromClinicName(data.name);
    const clinicData = {
      partnerId: data.partnerId,
      name: data.name,
      address: {
        street: data.address_street || "",
        city: data.address_city || "",
        state: data.address_state || "",
        country: (formData.address?.country || data.address_country || "").trim(),
        zipCode: data.address_zipCode || ""
      },
      phoneNumber: data.phoneNumber || "",
      email: data.email || "",
      website: data.website || "",
      defaultCurrency: data.defaultCurrency || "USD",
      availability: data.availability !== undefined ? data.availability : true,
      isDefault: data.isDefault || false,
      status: data.status || "draft",
      ...(slugHint ? { landingSlug: slugHint } : {}),
    };

    http
      .post("/api/v1/partners/medical/clinics", clinicData, { suppressInlineErrorSwal: true })
      .then((res) => {
        if (res.data?.success) {
          swal("Success", "Clinic created successfully", "success");
          fetchClinics();
          setModal({ add: false });
          reset();
          setFormData({
            name: "",
            partnerId: "",
            address: { street: "", city: "", state: "", country: "", zipCode: "" },
            phoneNumber: "",
            email: "",
            website: "",
            defaultCurrency: "USD",
            availability: true,
            isDefault: false,
            status: "draft"
          });
        } else {
          throw new Error(res.data?.message || "Create failed");
        }
      })
      .catch((e) => {
        swal("Clinic could not be created", formatAdminApiError(e, "Error creating clinic"), "error");
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      partnerId: "",
      address: { street: "", city: "", state: "", country: "", zipCode: "" },
      phoneNumber: "",
      email: "",
      website: "",
      defaultCurrency: "USD",
      availability: true,
      isDefault: false,
      status: "draft"
    });
    setNewPhotoFiles([]);
    reset();
  };

  const getClinicPhotoUrl = (photoPath) => {
    if (!photoPath) return 'https://via.placeholder.com/120?text=Photo';
    
    // Handle both string and object formats
    const path = typeof photoPath === 'string' ? photoPath : (photoPath.url || photoPath.path || photoPath);
    
    if (!path) return 'https://via.placeholder.com/120?text=Photo';
    
    const baseUrl = process.env.REACT_APP_API_URL || 'https://www.backend.inoqare.com';
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (path.startsWith('/')) {
      return `${baseUrl}${path}`;
    }
    return `${baseUrl}/uploads/${path}`;
  };

  const refetchEditClinic = () => {
    if (!editClinic?._id) return;
    http.get(`/api/v1/partners/medical/clinics/${editClinic._id}`).then((res) => {
      const clinic = res.data?.data || res.data;
      if (clinic) {
        const rawCases = Array.isArray(clinic.beforeAfterCases) ? clinic.beforeAfterCases : [];
        const beforeAfterCases = dedupeBeforeAfterCases(rawCases);
        setEditClinic((prev) => ({
          ...prev,
          ...clinic,
          beforeAfterCases,
          introVideoUrl: clinic.introVideoUrl || "",
          testimonialVideos: Array.isArray(clinic.testimonialVideos)
            ? clinic.testimonialVideos.map((v) => ({ title: v?.title || "", url: v?.url || "", thumbnail: v?.thumbnail || null }))
            : []
        }));
        if (Array.isArray(clinic.photos)) {
          setExistingPhotos(clinic.photos.map((p) => (typeof p === "string" ? p : p.url || p.path || p)));
        }
      }
    }).catch(() => {});
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        swal("Error", `${file.name} is not an image file`, "error");
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        swal("Error", `${file.name} must be less than 5MB`, "error");
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setNewPhotoFiles(prev => [...prev, ...validFiles]);
    }
    
    e.target.value = '';
  };

  const handleRemoveNewPhoto = (index) => {
    setNewPhotoFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeletePhoto = async (photoIndex) => {
    if (!editClinic?._id) {
      swal("Error", "Cannot delete photo: clinic not saved", "error");
      return;
    }
    
    try {
      await http.delete(`/api/v1/partners/medical/clinics/${editClinic._id}/photos/${photoIndex}`, {
        headers: { Authorization: `Bearer ${token}` },
        suppressInlineErrorSwal: true,
      });
      
      setExistingPhotos(prev => prev.filter((_, i) => i !== photoIndex));
      swal("Success", "Photo deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting photo:", error);
      swal("Photo could not be deleted", formatAdminApiError(error, "Error deleting photo"), "error");
    }
  };

  const handleIntroVideoStageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      swal("Error", "Please select a video file (e.g. MP4, WebM)", "error");
      return;
    }
    setIntroVideoStagedFile(file);
    e.target.value = "";
  };

  const handleIntroVideoSendFile = () => {
    if (!introVideoStagedFile || !editClinic?._id) return;
    setUploadingIntroVideo(true);
    const uploadForm = new FormData();
    uploadForm.append("file", introVideoStagedFile);
    http
      .post(
        `/api/v1/partners/medical/clinics/${editClinic._id}/intro-video`,
        uploadForm,
        {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
          suppressInlineErrorSwal: true,
        }
      )
      .then((res) => {
        const url = res.data?.url || res.data?.data?.url;
        if (url) setEditClinic((prev) => ({ ...prev, introVideoUrl: url }));
        setIntroVideoStagedFile(null);
        swal("Success", "Intro video uploaded", "success");
        refetchEditClinic();
      })
      .catch((err) =>
        swal("Intro video upload failed", formatAdminApiError(err, "Upload failed"), "error")
      )
      .finally(() => setUploadingIntroVideo(false));
  };

  const handleIntroVideoSaveUrl = () => {
    if (!editClinic?._id) return;
    const url = (editClinic.introVideoUrl || "").trim();
    if (!url) return;
    setUploadingIntroVideo(true);
    http
      .put(`/api/v1/partners/medical/clinics/${editClinic._id}`, { introVideoUrl: url }, {
        suppressInlineErrorSwal: true,
      })
      .then(() => swal("Success", "Hero video URL saved", "success"))
      .catch((err) =>
        swal("Video URL could not be saved", formatAdminApiError(err, "Save failed"), "error")
      )
      .finally(() => setUploadingIntroVideo(false));
  };

  const handleDeleteIntroVideo = () => {
    if (!editClinic?._id) return;
    setUploadingIntroVideo(true);
    http
      .delete(`/api/v1/partners/medical/clinics/${editClinic._id}/intro-video`, {
        suppressInlineErrorSwal: true,
      })
      .then(() => {
        setEditClinic((prev) => ({ ...prev, introVideoUrl: "" }));
        swal("Success", "Intro video removed", "success");
      })
      .catch((err) =>
        swal("Intro video could not be removed", formatAdminApiError(err, "Delete failed"), "error")
      )
      .finally(() => setUploadingIntroVideo(false));
  };

  const doTestimonialVideosUpload = (files) => {
    const valid = Array.from(files).filter((f) => f.type && f.type.startsWith("video/"));
    if (valid.length === 0 || !editClinic?._id) {
      if (valid.length === 0) swal("Error", "Please select video files (e.g. MP4, WebM)", "error");
      return;
    }
    setUploadingTestimonialVideos(true);
    const uploadForm = new FormData();
    valid.forEach((f) => uploadForm.append("files", f));
    http
      .post(
        `/api/v1/partners/medical/clinics/${editClinic._id}/testimonial-videos`,
        uploadForm,
        { headers: { Authorization: `Bearer ${token}` }, suppressInlineErrorSwal: true }
      )
      .then(() => {
        swal("Success", `${valid.length} testimonial video(s) uploaded`, "success");
        refetchEditClinic();
      })
      .catch((err) =>
        swal("Testimonial video upload failed", formatAdminApiError(err, "Upload failed"), "error")
      )
      .finally(() => setUploadingTestimonialVideos(false));
  };

  const handleTestimonialVideosUpload = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    doTestimonialVideosUpload(e.target.files);
    e.target.value = "";
  };

  const handleTestimonialDrop = (e) => {
    e.preventDefault();
    setTestimonialDragOver(false);
    if (e.dataTransfer?.files?.length > 0) doTestimonialVideosUpload(e.dataTransfer.files);
  };

  const handleDeleteTestimonialVideo = async (index) => {
    if (!editClinic?._id) return;
    try {
      await http.delete(`/api/v1/partners/medical/clinics/${editClinic._id}/testimonial-videos/${index}`, {
        suppressInlineErrorSwal: true,
      });
      setEditClinic((prev) => ({
        ...prev,
        testimonialVideos: (prev.testimonialVideos || []).filter((_, i) => i !== index)
      }));
      swal("Success", "Testimonial video removed", "success");
    } catch (err) {
      swal("Testimonial could not be removed", formatAdminApiError(err, "Delete failed"), "error");
    }
  };

  const mediaSelectorModalTitle = {
    banner: "Select banner from your library",
    photos: "Select photos from your library",
    beforeAfter: "Select images from your library",
    testimonials: "Select testimonials from your library",
  };
  const mediaSelectorConfirmLabel = {
    banner: "Use this",
    photos: "Add selected photos",
    beforeAfter: "Add selected images",
    testimonials: "Add selected",
  };

  const handleMediaSelectorSelect = (selectedItems) => {
    if (!selectedItems?.length || !editClinic) return;
    if (pickerMode === "banner") {
      const first = selectedItems[0];
      setEditClinic((prev) => ({ ...prev, introVideoUrl: first?.fileUrl || "" }));
      swal("Success", "Banner updated.", "success");
    } else if (pickerMode === "photos") {
      const urls = selectedItems.map((i) => i.fileUrl).filter(Boolean);
      setExistingPhotos((prev) => [...prev, ...urls]);
      swal("Success", `${urls.length} photo(s) added.`, "success");
    } else if (pickerMode === "beforeAfter") {
      const current = editClinic.beforeAfterCases ?? [];
      const maxOrder = current.length ? Math.max(...current.map((c) => Number(c.order) ?? 0)) : 0;
      const appended = selectedItems.map((item, idx) => ({
        title: item.title || "",
        imageUrl: item.fileUrl || "",
        order: maxOrder + idx + 1,
      }));
      setEditClinic((prev) => ({
        ...prev,
        beforeAfterCases: dedupeBeforeAfterCases([...(prev.beforeAfterCases ?? []), ...appended]),
      }));
      swal("Success", `${appended.length} image(s) added.`, "success");
    } else if (pickerMode === "testimonials") {
      const appended = selectedItems.map((item) => ({
        title: item.title || "",
        url: item.fileUrl || "",
        thumbnail: item.thumbnailUrl || null,
      }));
      setEditClinic((prev) => ({
        ...prev,
        testimonialVideos: [...(prev.testimonialVideos ?? []), ...appended],
      }));
      swal("Success", `${appended.length} testimonial(s) added.`, "success");
    }
    setShowMediaSelector(false);
  };

  const handleCopyFromClinicSubmit = async () => {
    const id = copyFromClinicId?.trim();
    if (!id || !editClinic) return;
    const types = copyFromClinicTypes;
    if (!types.banner && !types.photos && !types.beforeAfter && !types.testimonials) {
      swal("Warning", "Please select at least one type to copy.", "warning");
      return;
    }
    setCopyFromClinicLoading(true);
    try {
      const res = await http.get(`/api/v1/partners/medical/clinics/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        suppressInlineErrorSwal: true,
      });
      const source = res.data?.data || res.data;
      if (!source) {
        swal("Error", "Clinic not found.", "error");
        return;
      }
      setEditClinic((prev) => {
        let next = { ...prev };
        if (types.banner && source.introVideoUrl) next = { ...next, introVideoUrl: source.introVideoUrl };
        if (types.beforeAfter && Array.isArray(source.beforeAfterCases)) {
          const merged = [...(prev.beforeAfterCases || []), ...source.beforeAfterCases];
          next = { ...next, beforeAfterCases: dedupeBeforeAfterCases(merged) };
        }
        if (types.testimonials && Array.isArray(source.testimonialVideos)) {
          next = { ...next, testimonialVideos: source.testimonialVideos };
        }
        return next;
      });
      if (types.photos && Array.isArray(source.photos)) {
        const photoUrls = source.photos.map((p) => (typeof p === "string" ? p : p.url || p.path || p));
        setExistingPhotos((prev) => [...prev, ...photoUrls]);
      }
      setShowCopyFromClinic(false);
      setCopyFromClinicId("");
      setCopyFromClinicTypes({ banner: false, photos: false, beforeAfter: false, testimonials: false });
      swal("Success", "Media copied to this page. You can change or remove them below, then save.", "success");
    } catch (err) {
      swal("Could not load source clinic", formatAdminApiError(err, "Failed to load clinic"), "error");
    } finally {
      setCopyFromClinicLoading(false);
    }
  };

  const openMediaSelector = (mode) => {
    setPickerMode(mode);
    setShowMediaSelector(true);
  };
  const openCopyFromClinic = () => setShowCopyFromClinic(true);

  const clinicsForCopyDropdown = useMemo(() => {
    if (!Array.isArray(clinics) || !editClinic) return [];
    const currentId = editClinic._id || editClinic.id;
    return clinics
      .filter((c) => (c._id || c.id) !== currentId)
      .map((c) => ({ value: c._id || c.id, label: c.name || `Clinic ${c._id || c.id}` }));
  }, [clinics, editClinic]);

  return (
    <React.Fragment>
      <Head title="Clinics" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Clinics Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>
                  {initialLoading ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Loading...
                    </span>
                  ) : (
                    `You have ${Array.isArray(filteredClinics) ? filteredClinics.length : 0} clinic(s) in total.`
                  )}
                  {error && (
                    <span className="text-danger ms-2">
                      <Icon name="alert-circle" className="me-1" />
                      {error}
                    </span>
                  )}
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button className={`btn-icon btn-trigger toggle-expand mr-n1 ${false ? "active" : ""}`}>
                  <Icon name="menu-alt-r"></Icon>
                </Button>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <DataTable className="card-stretch" scrollable>
            <div className="card-inner position-relative card-tools-toggle">
              <div className="card-title-group">
                <div className="card-tools"></div>
                <div className="card-tools mr-n1">
                  <ul className="btn-toolbar gx-1">
                    <li>
                      <a
                        href="#search"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleSearch();
                        }}
                        className="btn btn-icon search-toggle toggle-search"
                      >
                        <Icon name="search"></Icon>
                      </a>
                    </li>
                    <li className="btn-toolbar-sep"></li>
                    <li>
                      <Button 
                        color="primary" 
                        onClick={() => {
                          resetForm();
                          setInitialAddFormSnapshot({
                            name: "",
                            partnerId: "",
                            address: { street: "", city: "", state: "", country: "", zipCode: "" },
                            phoneNumber: "",
                            email: "",
                            website: "",
                            defaultCurrency: "USD",
                            availability: true,
                            isDefault: false,
                            status: "draft",
                          });
                          setModal({ add: true });
                        }}
                        disabled={loading}
                      >
                        <Icon name="plus" />
                        <span>Add Clinic</span>
                      </Button>
                    </li>
                    <li>
                      <div className="toggle-wrap">
                        <Button className={`btn-icon btn-trigger toggle ${tablesm ? "active" : ""}`} onClick={() => updateTableSm(true)}>
                          <Icon name="menu-right"></Icon>
                        </Button>
                        <div className={`toggle-content ${tablesm ? "content-active" : ""}`}>
                          <ul className="btn-toolbar gx-1">
                            <li className="toggle-close">
                              <Button className="btn-icon btn-trigger toggle" onClick={() => updateTableSm(false)}>
                                <Icon name="arrow-left"></Icon>
                              </Button>
                            </li>
                            <li>
                              <div className="filter-wg dropdown-menu-xl" style={{ overflow: "visible" }}>
                                <div className="dropdown-head">
                                  <span className="sub-title dropdown-title">Filter Clinics</span>
                                </div>
                                <div className="dropdown-body dropdown-body-rg">
                                  <Row className="gx-6 gy-3">
                                    <Col size="6">
                                      <FormGroup>
                                        <label className="overline-title overline-title-alt">Status</label>
                                        <RSelect
                                          options={statusOptions}
                                          value={statusOptions.find((o) => o.value === filters.status)}
                                          onChange={(e) => onFilterChange(e, "status")}
                                          placeholder="Select Status"
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col size="6">
                                      <FormGroup>
                                        <label className="overline-title overline-title-alt">Partner</label>
                                        <RSelect
                                          options={partners.map((p) => ({
                                            value: p._id,
                                            label: `${p.organization_name || p._id}${p.contact_person_email ? ` (${p.contact_person_email})` : ""}`,
                                          }))}
                                          value={
                                            filters.partner
                                              ? (() => {
                                                  const p = partners.find((x) => x._id === filters.partner);
                                                  return { value: filters.partner, label: p ? `${p.organization_name || p._id}${p.contact_person_email ? ` (${p.contact_person_email})` : ""}` : filters.partner };
                                                })()
                                              : null
                                          }
                                          onChange={(e) => onFilterChange(e, "partner")}
                                          placeholder="Select Partner"
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col size="6">
                                      <FormGroup>
                                        <label className="overline-title overline-title-alt">Country</label>
                                        <RSelect
                                          options={countries}
                                          value={countries.find((o) => o.value === filters.country)}
                                          onChange={(e) => onFilterChange(e, "country")}
                                          placeholder="Select Country"
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col size="6">
                                      <FormGroup>
                                        <label className="overline-title overline-title-alt">Created Date</label>
                                        <input type="date" className="form-control" value={filters.date} onChange={(e) => onFilterChange(e, "date")} />
                                      </FormGroup>
                                    </Col>
                                    <Col size="12">
                                      <div className="d-flex justify-content-between">
                                        <Button color="primary" onClick={applyFilter}>
                                          Apply Filter
                                        </Button>
                                        <Button color="secondary" onClick={resetFilter}>
                                          Reset Filter
                                        </Button>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className={`card-search search-wrap ${!onSearch && "active"}`}>
                <div className="card-body">
                  <div className="search-content">
                    <Button
                      className="search-back btn-icon toggle-search active"
                      onClick={() => {
                        setOnSearchText("");
                        toggleSearch();
                      }}
                    >
                      <Icon name="arrow-left"></Icon>
                    </Button>
                    <input
                      type="text"
                      className="border-transparent form-focus-none form-control"
                      placeholder="Search by clinic, partner, email, city, country"
                      value={onSearchText}
                      onChange={(e) => onFilterChange(e)}
                    />
                    <Button className="search-submit btn-icon">
                      <Icon name="search"></Icon>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <DataTableBody compact>
              <DataTableHead>
                <DataTableRow size="lg">
                  <span className="sub-text">Clinic</span>
                </DataTableRow>
                <DataTableRow size="lg">
                  <span className="sub-text">Partner</span>
                </DataTableRow>
                <DataTableRow size="lg" className="col-hide-on-sm">
                  <span className="sub-text">Address</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow size="md" className="col-hide-on-sm">
                  <span className="sub-text">Created</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-right"></DataTableRow>
              </DataTableHead>

              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                currentItems.map((c, index) => (
                  <DataTableItem key={c?._id || c?.id || `clinic-${index}`}>
                    <DataTableRow size="lg" dataLabel="Clinic">
                      <ReadableCell>{c?.name || "-"}</ReadableCell>
                      <div className="small text-soft">{c?.isDefault ? "Default" : ""}</div>
                    </DataTableRow>
                    <DataTableRow size="lg" dataLabel="Partner">
                      <div>
                        {!c?.partner || !c?.partner?._id ? (
                          <span className="badge bg-danger">⚠️ Partner missing</span>
                        ) : (
                          <>
                            <div className="d-flex align-items-center mb-1">
                              <ReadableCell className="fw-bold">{c.partner.organization_name || "-"}</ReadableCell>
                              {c.partner.status && (
                                <span className={`badge ms-2 ${
                                  c.partner.status === 'active' ? 'bg-success' : 
                                  c.partner.status === 'pending' ? 'bg-warning' : 
                                  'bg-secondary'
                                }`}>
                                  {c.partner.status}
                                </span>
                              )}
                            </div>
                            
                            {c.partner.contact_person_email && (
                              <div className="text-soft small mb-1">
                                <Icon name="mail" className="me-1" />
                                <ReadableCell>{c.partner.contact_person_email}</ReadableCell>
                              </div>
                            )}
                            
                            {/* Vrai propriétaire (userId) */}
                            {c.partner.userId && (
                              <div className="text-soft small mb-1">
                                <Icon name="user" className="me-1" />
                                Owner: {c.partner.userId.email || c.partner.userId.first_name || 'N/A'}
                                {c.partner.userId.role && (
                                  <span className="badge bg-info ms-2">{c.partner.userId.role}</span>
                                )}
                              </div>
                            )}
                            
                            {/* Lien vers profil partner */}
                            <a 
                              href={getPublicUrl(`medical-partner-details/${c.partner._id}`)}
                              className="btn btn-sm btn-outline-primary mt-1"
                              onClick={(e) => {
                                e.preventDefault();
                                history.push(getPublicUrl(`medical-partner-details/${c.partner._id}`));
                              }}
                            >
                              View profile
                            </a>
                          </>
                        )}
                      </div>
                    </DataTableRow>
                    <DataTableRow size="lg" className="col-hide-on-sm" dataLabel="Address">
                      <div>
                        <ReadableCell title={[c?.address?.address, c?.address?.city, c?.address?.country].filter(Boolean).join(", ") || "-"}>
                          {[c?.address?.address, c?.address?.city, c?.address?.country].filter(Boolean).join(", ") || "-"}
                        </ReadableCell>
                      </div>
                    </DataTableRow>
                    <DataTableRow size="md" dataLabel="Status">
                      <span>{c?.status || "-"}</span>
                    </DataTableRow>
                    <DataTableRow size="md" className="col-hide-on-sm" dataLabel="Created">
                      <span>{c?.createdAt ? moment(c.createdAt).format("DD/MM/YY") : "-"}</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools" dataLabel="Actions">
                      <ul className="nk-tb-actions gx-1">
                        {/* Actions pour les cliniques en attente */}
                        {c?.status === "pending" && (
                          <>
                            <li className="nk-tb-action-hidden">
                              <TooltipComponent
                                tag="a"
                                containerClassName="btn btn-trigger btn-icon text-success"
                                id={`approve-clinic-${c?._id || c?.id || Math.random()}`}
                                icon="check-fill"
                                direction="top"
                                text="Approve"
                                onClick={(e) => {
                                  e.preventDefault();
                                  confirmUpdateClinic(c, "active");
                                }}
                              />
                            </li>
                            <li className="nk-tb-action-hidden">
                              <TooltipComponent
                                tag="a"
                                containerClassName="btn btn-trigger btn-icon text-danger"
                                id={`reject-clinic-${c?._id || c?.id || Math.random()}`}
                                icon="cross-fill"
                                direction="top"
                                text="Reject"
                                onClick={(e) => {
                                  e.preventDefault();
                                  confirmUpdateClinic(c, "inactive");
                                }}
                              />
                            </li>
                          </>
                        )}
                        <li className="nk-tb-action-hidden">
                          <TooltipComponent 
                            id={`edit-${c?._id || c?.id || Math.random()}`} 
                            tag="a" 
                            containerClassName="btn btn-trigger btn-icon" 
                            icon="edit-alt-fill" 
                            direction="top" 
                            text="Edit"
                            onClick={(e) => {
                              e.preventDefault();
                              onEditClick(c);
                            }}
                          />
                        </li>
                        <li className="nk-tb-action-hidden">
                          <TooltipComponent 
                            id={`prices-${c?._id || c?.id || Math.random()}`} 
                            tag="a" 
                            containerClassName="btn btn-trigger btn-icon text-primary" 
                            icon="calc" 
                            direction="top" 
                            text="Manage prices"
                            onClick={(e) => {
                              e.preventDefault();
                              const clinicId = c?._id || c?.id;
                              if (clinicId) {
                                history.push(getPublicUrl(`clinics/${clinicId}/prices`));
                              } else {
                                swal("Error", "Missing clinic ID", "error");
                              }
                            }}
                          />
                        </li>
                        <li className="nk-tb-action-hidden">
                          <TooltipComponent
                            id={`practitioners-${c?._id || c?.id || Math.random()}`}
                            tag="a"
                            containerClassName="btn btn-trigger btn-icon text-secondary"
                            icon="users"
                            direction="top"
                            text="Practitioners"
                            onClick={(e) => {
                              e.preventDefault();
                              const clinicId = c?._id || c?.id;
                              if (clinicId) {
                                history.push(getPublicUrl(`clinics/${clinicId}/practitioners`));
                              } else {
                                swal("Error", "Missing clinic ID", "error");
                              }
                            }}
                          />
                        </li>
                        <li className="nk-tb-action-hidden">
                          <TooltipComponent
                            id={`cal-${c?._id || c?.id || Math.random()}`}
                            tag="a"
                            containerClassName="btn btn-trigger btn-icon text-info"
                            icon="calendar"
                            direction="top"
                            text="TC calendar / slots sync"
                            onClick={(e) => {
                              e.preventDefault();
                              const clinicId = c?._id || c?.id;
                              if (clinicId) {
                                history.push(getPublicUrl(`clinics/${clinicId}/calendar`));
                              } else {
                                swal("Error", "Missing clinic ID", "error");
                              }
                            }}
                          />
                        </li>
                        {c?.status !== "inactive" && (
                          <li className="nk-tb-action-hidden">
                            <TooltipComponent 
                              id={`suspend-${c?._id || c?.id || Math.random()}`} 
                              tag="a" 
                              containerClassName="btn btn-trigger btn-icon" 
                              icon="user-cross-fill" 
                              direction="top" 
                              text="Suspend"
                              onClick={(e) => {
                                e.preventDefault();
                                onSuspend(c);
                              }}
                            />
                          </li>
                        )}
                        <li className="nk-tb-action-hidden">
                          <TooltipComponent 
                            id={`delete-${c?._id || c?.id || Math.random()}`} 
                            tag="a" 
                            containerClassName="btn btn-trigger btn-icon" 
                            icon="delete" 
                            direction="top" 
                            text="Delete"
                            onClick={(e) => {
                              e.preventDefault();
                              onDelete(c);
                            }}
                          />
                        </li>
                      </ul>
                    </DataTableRow>
                  </DataTableItem>
                ))
              ) : (
                <div className="text-center">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </DataTableBody>

            <div className="card-inner">
              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                <PaginationComponent itemPerPage={itemPerPage} totalItems={Array.isArray(filteredClinics) ? filteredClinics.length : 0} paginate={setCurrentPage} currentPage={currentPage} />
              ) : (
                <div className="text-center">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </div>
          </DataTable>
        </Block>

        <AdminFormModal
          isOpen={modal.edit}
          toggle={exitEditModal}
          title="Edit clinic"
          subtitle={
            editClinic
              ? `${editClinic?.name || editClinic?.clinicName || "Clinic"} · Status: ${editClinic?.status || "—"}`
              : ""
          }
          actions={
            editClinic?._id ? (
              <>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  outline
                  onClick={() => history.push(getPublicUrl(`clinics/${editClinic._id}/prices`))}
                >
                  Prices
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  outline
                  onClick={() => history.push(getPublicUrl(`clinics/${editClinic._id}/calendar`))}
                >
                  Calendar
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  outline
                  onClick={() => history.push(getPublicUrl(`clinics/${editClinic._id}/practitioners`))}
                >
                  Practitioners
                </Button>
              </>
            ) : null
          }
          footer={
            <>
              <Button color="light" type="button" onClick={exitEditModal}>
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                form="clinic-edit-form"
                disabled={loading || uploadingPhotos || uploadingIntroVideo || uploadingTestimonialVideos}
              >
                {loading || uploadingPhotos || uploadingIntroVideo || uploadingTestimonialVideos ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {uploadingPhotos
                      ? "Uploading photos…"
                      : uploadingIntroVideo
                      ? "Uploading intro video…"
                      : uploadingTestimonialVideos
                      ? "Uploading testimonial videos…"
                      : "Saving…"}
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </>
          }
        >
              <div className="px-1">
                {loading && (
                  <div className="overlay-loading" style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(255,255,255,0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                  }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
                <Form id="clinic-edit-form" className="row gy-4" onSubmit={handleSubmit(onSaveEdit)}>
                  <Col md="12">
                    <h6 className="overline-title">Basic Information</h6>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Clinic name *</label>
                      <input
                        className="form-control"
                        type="text"
                        name="name"
                        defaultValue={editClinic?.name || ""}
                        ref={register({ required: "Clinic name is required" })}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, name: e.target.value }))}
                      />
                      {errors.name && <span className="invalid-feedback d-block">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        options={statusOptions}
                        value={statusOptions.find((o) => o.value === (editClinic?.status || "draft")) || { value: "draft", label: "Draft"}}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, status: e.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <div className="alert alert-light border small mb-3">
                      <strong>Stable IDs (canonical references)</strong>
                      <div className="mt-1">
                        Clinic ID: <code>{editClinic?._id || "—"}</code>
                      </div>
                      <div>
                        Partner ID:{" "}
                        <code>
                          {editClinic?.partner
                            ? typeof editClinic.partner === "object"
                              ? editClinic.partner._id || "—"
                              : editClinic.partner
                            : "—"}
                        </code>
                      </div>
                      <p className="text-muted mb-0 mt-2 small">
                        Use these IDs in integrations and deep links to avoid collisions between partner landing slugs and clinic slugs.
                      </p>
                    </div>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Slug (landing URL)</label>
                      <div className="d-flex flex-g-2 align-items-center">
                        <input
                          className="form-control"
                          type="text"
                          name="landingSlug"
                          placeholder="e.g. gaia-clinic"
                          value={editClinic?.landingSlug ?? ""}
                          ref={register}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, landingSlug: e.target.value.trim() || null }))}
                        />
                        <Button
                          type="button"
                          color="light"
                          size="sm"
                          onClick={() => {
                            const name = (editClinic?.name || editClinic?.clinicName || "").trim() || "clinic";
                            const generated = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
                            setEditClinic((prev) => ({ ...prev, landingSlug: generated || null }));
                          }}
                        >
                          Generate from name
                        </Button>
                      </div>
                      <small className="form-text text-muted">Lowercase, hyphens only (a-z, 0-9, -). Must be unique. Used in URLs: /dental/country/city/your-slug</small>
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Form options source (category group)</label>
                      <RSelect
                        options={[
                          { value: "", label: "None (use clinic categories)" },
                          ...categoryOptions,
                        ]}
                        value={
                          editClinic?.formOptionsRootCategoryId
                            ? (categoryOptions.find((o) => o.value === editClinic.formOptionsRootCategoryId) || { value: editClinic.formOptionsRootCategoryId, label: editClinic.formOptionsRootCategoryId })
                            : { value: "", label: "None (use clinic categories)" }
                        }
                        onChange={(opt) => setEditClinic((prev) => ({ ...prev, formOptionsRootCategoryId: opt?.value ?? "" }))}
                        placeholder="None"
                      />
                      <small className="form-text text-muted">Category whose children are shown as &quot;What do you need?&quot; options on the clinic landing. Leave &quot;None&quot; to use clinic categories.</small>
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <h6 className="overline-title">Address</h6>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Street</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_street"
                        defaultValue={editClinic?.address?.street || editClinic?.address?.address || ""}
                        ref={register}
                        onChange={(e) => setEditClinic((prev) => ({ 
                          ...prev, 
                          address: { ...(prev?.address || {}), street: e.target.value, address: e.target.value } 
                        }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">City *</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_city"
                        defaultValue={editClinic?.address?.city || ""}
                        ref={register({ required: "City is required" })}
                        onChange={(e) => setEditClinic((prev) => ({ 
                          ...prev, 
                          address: { ...(prev?.address || {}), city: e.target.value } 
                        }))}
                      />
                      {errors.address_city && <span className="invalid-feedback d-block">{errors.address_city.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="edit-clinic-country">
                        Country * (ISO)
                      </label>
                      <RSelect
                        inputId="edit-clinic-country"
                        options={mergeCountryFilterOptions([
                          editClinic?.address?.country,
                          ...clinics.map((c) => c?.address?.country),
                        ])}
                        value={resolveIsoCountrySelectValue(editClinic?.address?.country) || null}
                        onChange={(opt) =>
                          setEditClinic((prev) => ({
                            ...prev,
                            address: { ...(prev?.address || {}), country: opt?.value ? String(opt.value).trim() : "" },
                          }))
                        }
                        placeholder="Select country…"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">State/Region</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_state"
                        defaultValue={editClinic?.address?.state || ""}
                        ref={register}
                        onChange={(e) => setEditClinic((prev) => ({ 
                          ...prev, 
                          address: { ...(prev?.address || {}), state: e.target.value } 
                        }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Postal code</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_zipCode"
                        defaultValue={editClinic?.address?.zipCode || ""}
                        ref={register}
                        onChange={(e) => setEditClinic((prev) => ({ 
                          ...prev, 
                          address: { ...(prev?.address || {}), zipCode: e.target.value } 
                        }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <h6 className="overline-title">Contact</h6>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        type="text"
                        name="phoneNumber"
                        defaultValue={editClinic?.phoneNumber || ""}
                        ref={register}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Email</label>
                      <input
                        className="form-control"
                        type="email"
                        name="email"
                        defaultValue={editClinic?.email || ""}
                        ref={register({
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, email: e.target.value }))}
                      />
                      {errors.email && <span className="invalid-feedback d-block">{errors.email.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Website</label>
                      <input
                        className="form-control"
                        type="url"
                        name="website"
                        defaultValue={editClinic?.website || ""}
                        ref={register}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, website: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Default currency</label>
                      <RSelect
                        options={[
                          { value: "USD", label: "USD - Dollar US" },
                          { value: "EUR", label: "EUR - Euro" },
                          { value: "GBP", label: "GBP - Livre Sterling" },
                          { value: "TRY", label: "TRY - Lire Turque" },
                          { value: "TND", label: "TND - Dinar Tunisien" },
                          { value: "MAD", label: "MAD - Dirham Marocain" }
                        ]}
                        value={{ 
                          value: editClinic?.defaultCurrency || "USD", 
                          label: editClinic?.defaultCurrency || "USD" 
                        }}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, defaultCurrency: e.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="editAvailability"
                          checked={editClinic?.availability !== false}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, availability: e.target.checked }))}
                        />
                        <label className="custom-control-label" htmlFor="editAvailability">
                          Available
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="editIsDefault"
                          checked={editClinic?.isDefault || false}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, isDefault: e.target.checked }))}
                        />
                        <label className="custom-control-label" htmlFor="editIsDefault">
                          Default clinic
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="editOffersTeleconsultation"
                          checked={editClinic?.offersTeleconsultation === true}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, offersTeleconsultation: e.target.checked }))}
                        />
                        <label className="custom-control-label" htmlFor="editOffersTeleconsultation">
                          Offers teleconsultation
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="editOffersMedicalTourism"
                          checked={editClinic?.offersMedicalTourism !== false}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, offersMedicalTourism: e.target.checked }))}
                        />
                        <label className="custom-control-label" htmlFor="editOffersMedicalTourism">
                          Offers medical tourism (travel treatments)
                        </label>
                      </div>
                      <p className="text-soft small mb-0 mt-1">
                        When off, this clinic is hidden from quote matching, chat destination matching, and the public clinic directory. Teleconsultation listing still follows &quot;Offers teleconsultation&quot; and platform approval.
                      </p>
                    </FormGroup>
                  </Col>
                  {editClinic?.offersMedicalTourism === false && editClinic?.offersTeleconsultation !== true ? (
                    <Col md="12">
                      <div className="alert alert-warning py-2 small mb-0" role="status">
                        Both channels are off: patients will not see this clinic in teleconsultation or medical-tourism flows. Enable at least one unless the clinic is intentionally paused.
                      </div>
                    </Col>
                  ) : null}
                  <Col md="12">
                    <h6 className="overline-title">Prescription regulation (teleconsultation)</h6>
                    <p className="text-soft small mb-2">
                      Jurisdiction determines which country&apos;s prescription rules apply (e.g. FR, GB, IN, CA). Configure regulations in &quot;Prescription regulations&quot; menu.
                    </p>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <label className="form-label">Jurisdiction (country code)</label>
                      <RSelect
                        options={[
                          { value: "FR", label: "FR - France" },
                          { value: "GB", label: "GB - United Kingdom" },
                          { value: "IN", label: "IN - India" },
                          { value: "CA", label: "CA - Canada" },
                          { value: "US", label: "US - United States" },
                          { value: "", label: "— Default (FR)" }
                        ]}
                        value={(() => {
                          const j = editClinic?.jurisdiction || "";
                          const opts = [
                            { value: "FR", label: "FR - France" },
                            { value: "GB", label: "GB - United Kingdom" },
                            { value: "IN", label: "IN - India" },
                            { value: "CA", label: "CA - Canada" },
                            { value: "US", label: "US - United States" },
                            { value: "", label: "— Default (FR)" }
                          ];
                          return opts.find((o) => o.value === j) || opts[5];
                        })()}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, jurisdiction: e?.value || "FR" }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <label className="form-label">Clinic timezone (teleconsultation)</label>
                      <p className="text-soft small mb-1">
                        IANA timezone displayed to patients during booking (e.g. "Europe/Istanbul"). Required for international TC.
                      </p>
                      <select
                        className="form-control form-select"
                        value={editClinic?.timezone || ""}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, timezone: e.target.value || null }))}
                      >
                        <option value="">— Select a timezone —</option>
                        <optgroup label="Europe">
                          <option value="Europe/London">UK — London (GMT/BST)</option>
                          <option value="Europe/Paris">France — Paris (CET/CEST)</option>
                          <option value="Europe/Berlin">Germany — Berlin (CET/CEST)</option>
                          <option value="Europe/Rome">Italy — Rome (CET/CEST)</option>
                          <option value="Europe/Madrid">Spain — Madrid (CET/CEST)</option>
                          <option value="Europe/Tirane">Albania — Tirana (CET/CEST)</option>
                          <option value="Europe/Istanbul">Turkey — Istanbul (TRT, UTC+3)</option>
                          <option value="Europe/Athens">Greece — Athens (EET/EEST)</option>
                          <option value="Europe/Bucharest">Romania — Bucharest (EET/EEST)</option>
                          <option value="Europe/Sofia">Bulgaria — Sofia (EET/EEST)</option>
                          <option value="Europe/Warsaw">Poland — Warsaw (CET/CEST)</option>
                          <option value="Europe/Amsterdam">Netherlands — Amsterdam (CET/CEST)</option>
                          <option value="Europe/Zurich">Switzerland — Zurich (CET/CEST)</option>
                          <option value="Europe/Lisbon">Portugal — Lisbon (WET/WEST)</option>
                        </optgroup>
                        <optgroup label="Middle East / Africa">
                          <option value="Asia/Dubai">UAE — Dubai (GST, UTC+4)</option>
                          <option value="Asia/Riyadh">Saudi Arabia — Riyadh (AST, UTC+3)</option>
                          <option value="Asia/Jerusalem">Israel — Jerusalem (IST)</option>
                          <option value="Asia/Beirut">Lebanon — Beirut (EET)</option>
                          <option value="Africa/Cairo">Egypt — Cairo (EET)</option>
                          <option value="Africa/Tunis">Tunisia — Tunis (CET)</option>
                          <option value="Africa/Casablanca">Morocco — Casablanca (WET)</option>
                        </optgroup>
                        <optgroup label="Americas">
                          <option value="America/New_York">USA — New York (ET)</option>
                          <option value="America/Chicago">USA — Chicago (CT)</option>
                          <option value="America/Los_Angeles">USA — Los Angeles (PT)</option>
                          <option value="America/Toronto">Canada — Toronto (ET)</option>
                        </optgroup>
                        <optgroup label="Asia Pacific">
                          <option value="Australia/Sydney">Australia — Sydney (AEST)</option>
                        </optgroup>
                      </select>
                      {adminTimezonePreviewText ? (
                        <p className="text-soft small mb-0 mt-2">
                          Current time in this zone: <strong>{adminTimezonePreviewText}</strong>
                        </p>
                      ) : (
                        <p className="text-soft small mb-0 mt-2">Select a timezone to preview local time.</p>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <h6 className="overline-title mt-2">Teleconsultation settings</h6>
                    <p className="text-soft small mb-2">
                      Booking behaviour for teleconsultation at this clinic (visit length, buffers, booking window).
                    </p>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <label className="form-label">Default visit length (minutes)</label>
                      <input
                        type="number"
                        min={10}
                        max={60}
                        className="form-control"
                        value={editClinic?.consultationDurationMinutes ?? 20}
                        onChange={(e) =>
                          setEditClinic((prev) => ({
                            ...prev,
                            consultationDurationMinutes: Math.min(60, Math.max(10, Number(e.target.value) || 20)),
                          }))
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <label className="form-label">Buffer between visits (minutes)</label>
                      <input
                        type="number"
                        min={0}
                        max={180}
                        className="form-control"
                        placeholder="Optional"
                        value={editClinic?.tcBufferMinutes ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditClinic((prev) => ({
                            ...prev,
                            tcBufferMinutes: v === "" ? null : Math.min(180, Math.max(0, Number(v))),
                          }));
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <label className="form-label">Min booking delay (hours)</label>
                      <input
                        type="number"
                        min={0}
                        max={168}
                        className="form-control"
                        placeholder="Optional"
                        value={editClinic?.tcMinBookingDelayHours ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditClinic((prev) => ({
                            ...prev,
                            tcMinBookingDelayHours: v === "" ? null : Math.min(168, Math.max(0, Number(v))),
                          }));
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <label className="form-label">Max booking window (days)</label>
                      <input
                        type="number"
                        min={1}
                        max={120}
                        className="form-control"
                        placeholder="Optional"
                        value={editClinic?.tcMaxBookingDays ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditClinic((prev) => ({
                            ...prev,
                            tcMaxBookingDays: v === "" ? null : Math.min(120, Math.max(1, Number(v))),
                          }));
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <label className="form-label">Slot generation horizon (days)</label>
                      <input
                        type="number"
                        min={1}
                        max={120}
                        className="form-control"
                        placeholder="Optional"
                        value={editClinic?.tcSlotHorizonDays ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditClinic((prev) => ({
                            ...prev,
                            tcSlotHorizonDays: v === "" ? null : Math.min(120, Math.max(1, Number(v))),
                          }));
                        }}
                      />
                      <span className="form-note">Leave empty to use platform default. Must be ≥ max booking window.</span>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <label className="form-label">Emergency contact (optional)</label>
                      <input
                        className="form-control"
                        type="text"
                        placeholder="e.g. On-call phone"
                        defaultValue={editClinic?.emergencyContact || ""}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, emergencyContact: e.target.value.trim() || undefined }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <label className="form-label">Insurance status (optional)</label>
                      <input
                        className="form-control"
                        type="text"
                        placeholder="e.g. Sector 1/2"
                        defaultValue={editClinic?.insuranceStatus || ""}
                        onChange={(e) => setEditClinic((prev) => ({ ...prev, insuranceStatus: e.target.value.trim() || undefined }))}
                      />
                    </FormGroup>
                  </Col>

                  {/* Link to Prices */}
                  {editClinic?._id && (
                    <Col md="12">
                      <FormGroup>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            setModal({ edit: false });
                            history.push(getPublicUrl(`clinics/${editClinic._id}/prices`));
                          }}
                        >
                          <Icon name="calc" />
                          <span className="ms-2">Manage prices</span>
                        </Button>
                      </FormGroup>
                    </Col>
                  )}

                  {/* ─── Landing Media (single editor: introVideoUrl, testimonialVideos, beforeAfterCases, photos) ─── */}
                  <Col md="12">
                    <h6 className="overline-title mt-2">Landing media</h6>
                    <p className="text-soft small mb-3">
                      Hero banner, testimonials, before/after, and photos for the public clinic landing page. Upload, media library, or copy from
                      another clinic — same API fields as the partner dashboard clinic media page.
                    </p>
                  </Col>

                  {/* ── Bloc 1 : Banner (Hero) ───────────────────────────── */}
                  <Col md="12">
                    <div
                      className="card card-bordered mb-3"
                      style={{ borderLeft: "3px solid #6576ff" }}
                    >
                      <div className="card-inner py-3 px-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="video" className="me-2 text-primary" />
                          <strong className="small">Banner</strong>
                        </div>
                        <p className="text-soft small mb-3">
                          One image or one video at the top of your page. You can upload a new file, choose from your library, or use the same as another clinic.
                        </p>

                        {/* Three options */}
                        <div className="row g-2 mb-3">
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100" style={{ background: "#f8f9fa" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="upload" className="me-2 text-primary" />
                                <strong className="small">Upload new</strong>
                              </div>
                              <p className="text-soft small mb-2">Add from your computer or phone.</p>
                              {/* Toggle upload / URL */}
                              <div className="d-flex flex-g-2 mb-2">
                          <button
                            type="button"
                            className={`btn btn-sm ${introVideoMode === "upload" ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => { setIntroVideoMode("upload"); setIntroVideoStagedFile(null); }}
                          >
                            <Icon name="upload" className="me-1" /> Upload file
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${introVideoMode === "url" ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => setIntroVideoMode("url")}
                          >
                            <Icon name="link" className="me-1" /> URL
                          </button>
                        </div>

                        {introVideoMode === "upload" ? (
                          <div>
                            <label
                              className="btn btn-outline-primary btn-sm mb-2"
                              style={{ cursor: uploadingIntroVideo ? "wait" : "pointer" }}
                            >
                              <input
                                type="file"
                                accept="video/*"
                                onChange={handleIntroVideoStageFile}
                                disabled={uploadingIntroVideo}
                                style={{ display: "none" }}
                              />
                              <Icon name="clip" className="me-1" /> Choose video file
                            </label>
                            {introVideoStagedFile && (
                              <div className="d-flex align-items-center flex-g-2 mb-2 p-2 rounded" style={{ background: "#f5f6fa" }}>
                                <Icon name="file-text" className="text-primary" />
                                <span className="small text-truncate" style={{ maxWidth: "200px" }}>
                                  {introVideoStagedFile.name}
                                </span>
                                <span className="badge bg-light text-muted small">
                                  {(introVideoStagedFile.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <Button
                                  color="primary"
                                  size="sm"
                                  type="button"
                                  onClick={handleIntroVideoSendFile}
                                  disabled={uploadingIntroVideo}
                                >
                                  {uploadingIntroVideo ? (
                                    <><span className="spinner-border spinner-border-sm me-1" />Uploading…</>
                                  ) : (
                                    <><Icon name="send" className="me-1" />Send</>
                                  )}
                                </Button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-ghost-secondary"
                                  onClick={() => setIntroVideoStagedFile(null)}
                                  aria-label="Cancel"
                                >
                                  <Icon name="cross" />
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-g-2 align-items-start flex-wrap mb-2">
                            <input
                              type="url"
                              className="form-control form-control-sm flex-grow-1"
                              placeholder="YouTube or direct MP4 URL"
                              value={editClinic?.introVideoUrl || ""}
                              onChange={(e) => setEditClinic((prev) => ({ ...prev, introVideoUrl: e.target.value }))}
                              style={{ minWidth: "220px" }}
                            />
                            <Button
                              color="primary"
                              size="sm"
                              type="button"
                              onClick={handleIntroVideoSaveUrl}
                              disabled={uploadingIntroVideo || !(editClinic?.introVideoUrl || "").trim()}
                            >
                              {uploadingIntroVideo ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                "Save URL"
                              )}
                            </Button>
                          </div>
                        )}

                        {/* Current video status */}
                        {(editClinic?.introVideoUrl || "").trim() ? (
                          <div className="d-flex align-items-center flex-g-2 mt-2 p-2 rounded" style={{ background: "#f0fff4", border: "1px solid #d1fae5" }}>
                            <Icon name="check-circle" className="text-success" />
                            <span className="small text-truncate flex-grow-1" style={{ maxWidth: "240px" }}>
                              {editClinic.introVideoUrl}
                            </span>
                            {(editClinic.introVideoUrl.includes("youtube") || editClinic.introVideoUrl.includes("youtu.be")) ? (
                              <a href={editClinic.introVideoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                                <Icon name="external-link" className="me-1" />Open
                              </a>
                            ) : (
                              <video
                                src={editClinic.introVideoUrl.startsWith("http") ? editClinic.introVideoUrl : getClinicPhotoUrl(editClinic.introVideoUrl)}
                                controls
                                style={{ maxWidth: "200px", maxHeight: "120px", borderRadius: "4px" }}
                              />
                            )}
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              type="button"
                              onClick={handleDeleteIntroVideo}
                              disabled={uploadingIntroVideo}
                              aria-label="Remove intro video"
                            >
                              <Icon name="trash" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-soft small mb-0 mt-1">No hero video set.</p>
                        )}
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#f0f9ff" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="folder" className="me-2 text-info" />
                                <strong className="small">Choose from library</strong>
                              </div>
                              <p className="text-soft small mb-2">Pick something you already added to your media.</p>
                              <Button color="primary" size="sm" outline className="mt-auto" onClick={() => openMediaSelector("banner")}>
                                <Icon name="folder" className="me-1" /> Choose from library
                              </Button>
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#fefce8" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="copy" className="me-2" style={{ color: "#ca8a04" }} />
                                <strong className="small">Use from another clinic</strong>
                              </div>
                              <p className="text-soft small mb-2">Copy from an existing clinic so you don&apos;t add them again.</p>
                              <Button color="light" size="sm" className="mt-auto" onClick={openCopyFromClinic}>
                                <Icon name="copy" className="me-1" /> Use from another clinic
                              </Button>
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* ── Bloc 2 : Before & after ─────────────────────────── */}
                  <Col md="12">
                    <div
                      className="card card-bordered mb-3"
                      style={{ borderLeft: "3px solid #09c2de" }}
                    >
                      <div className="card-inner py-3 px-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="img" className="me-2" style={{ color: "#09c2de" }} />
                          <strong className="small">Before &amp; after</strong>
                          <span className="badge bg-light text-muted ms-2 small">
                            {(editClinic?.beforeAfterCases ?? []).length} / 20
                          </span>
                        </div>
                        <p className="text-soft small mb-3">
                          Add before-and-after cases. You can upload new images, choose from your library, or copy from another clinic.
                        </p>
                        <div className="row g-2 mb-3">
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100" style={{ background: "#f8f9fa" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="upload" className="me-2 text-primary" />
                                <strong className="small">Upload new</strong>
                              </div>
                              <p className="text-soft small mb-2">Add images from your computer or phone.</p>
                              <BeforeAfterEditor
                                cases={editClinic?.beforeAfterCases ?? []}
                                onChange={(next) => setEditClinic((prev) => ({ ...prev, beforeAfterCases: next }))}
                                getImageUrl={getClinicPhotoUrl}
                                disabled={loading || uploadingPhotos || uploadingIntroVideo || uploadingTestimonialVideos}
                                clinicId={editClinic?._id}
                                onRefresh={refetchEditClinic}
                              />
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#f0f9ff" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="folder" className="me-2 text-info" />
                                <strong className="small">Choose from library</strong>
                              </div>
                              <p className="text-soft small mb-2">Pick images you already added to your media library.</p>
                              <Button color="primary" size="sm" outline className="mt-auto" onClick={() => openMediaSelector("beforeAfter")}>
                                <Icon name="folder" className="me-1" /> Choose from library
                              </Button>
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#fefce8" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="copy" className="me-2" style={{ color: "#ca8a04" }} />
                                <strong className="small">Use from another clinic</strong>
                              </div>
                              <p className="text-soft small mb-2">Copy from an existing clinic so you don&apos;t add them again.</p>
                              <Button color="light" size="sm" className="mt-auto" onClick={openCopyFromClinic}>
                                <Icon name="copy" className="me-1" /> Use from another clinic
                              </Button>
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* ── Bloc 3 : Testimonials ───────────────────────────── */}
                  <Col md="12">
                    <div
                      className="card card-bordered mb-3"
                      style={{ borderLeft: "3px solid #f4bd0e" }}
                    >
                      <div className="card-inner py-3 px-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="play-circle" className="me-2" style={{ color: "#f4bd0e" }} />
                          <strong className="small">Testimonials</strong>
                          <span className="badge bg-light text-muted ms-2 small">
                            {(Array.isArray(editClinic?.testimonialVideos) ? editClinic.testimonialVideos : []).length}
                          </span>
                        </div>
                        <p className="text-soft small mb-3">
                          Add testimonial photos or videos. You can upload new files, choose from your library, or copy from another clinic.
                        </p>
                        <div className="row g-2 mb-3">
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100" style={{ background: "#f8f9fa" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="upload" className="me-2 text-primary" />
                                <strong className="small">Upload new</strong>
                              </div>
                              <p className="text-soft small mb-2">Add photos or videos from your computer or phone.</p>
                        {/* Drag-drop zone */}
                        <div
                          role="button"
                          tabIndex={0}
                          onDragOver={(e) => { e.preventDefault(); setTestimonialDragOver(true); }}
                          onDragLeave={(e) => { e.preventDefault(); setTestimonialDragOver(false); }}
                          onDrop={handleTestimonialDrop}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.preventDefault(); }}
                          className="mb-3"
                          style={{
                            border: `2px dashed ${testimonialDragOver ? "#f4bd0e" : "#dee2e6"}`,
                            borderRadius: "8px",
                            padding: "18px",
                            textAlign: "center",
                            background: testimonialDragOver ? "#fffbeb" : "#fff",
                            cursor: uploadingTestimonialVideos ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="file"
                            accept="video/*"
                            multiple
                            id="testimonial-bulk-upload"
                            onChange={handleTestimonialVideosUpload}
                            disabled={uploadingTestimonialVideos}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="testimonial-bulk-upload"
                            style={{ cursor: uploadingTestimonialVideos ? "wait" : "pointer", marginBottom: 0 }}
                          >
                            {uploadingTestimonialVideos ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Uploading testimonial videos…
                              </>
                            ) : (
                              <>
                                <Icon name="upload" className="me-2" />
                                Drag &amp; drop videos here, or click to select multiple
                              </>
                            )}
                          </label>
                        </div>

                        {/* URL add row */}
                        <div className="d-flex flex-g-2 align-items-center mb-3 flex-wrap">
                          <span className="text-soft small">Or add by URL:</span>
                          <Button
                            color="light"
                            size="sm"
                            type="button"
                            onClick={() => setEditClinic((prev) => ({
                              ...prev,
                              testimonialVideos: [...(prev.testimonialVideos || []), { title: "", url: "", thumbnail: null }],
                            }))}
                          >
                            <Icon name="plus" className="me-1" />
                            Add URL row
                          </Button>
                        </div>

                        {/* Testimonial list */}
                        {(Array.isArray(editClinic?.testimonialVideos) ? editClinic.testimonialVideos : []).length === 0 ? (
                          <p className="text-soft small mb-0">No testimonial videos yet.</p>
                        ) : (
                          <div className="list-group list-group-flush">
                            {(Array.isArray(editClinic?.testimonialVideos) ? editClinic.testimonialVideos : []).map((item, idx) => (
                              <div
                                key={idx}
                                className="list-group-item d-flex flex-g-2 align-items-start flex-wrap py-2 px-0"
                                style={{ borderBottom: "1px solid #f0f0f0" }}
                              >
                                <div className="d-flex flex-column flex-g-1 flex-grow-1" style={{ minWidth: "220px" }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Title (e.g. Patient testimonial 1)"
                                    value={item.title || ""}
                                    onChange={(e) => setEditClinic((prev) => {
                                      const arr = [...(prev.testimonialVideos || [])];
                                      arr[idx] = { ...arr[idx], title: e.target.value };
                                      return { ...prev, testimonialVideos: arr };
                                    })}
                                  />
                                  {item.url ? (
                                    <div className="d-flex align-items-center flex-g-1">
                                      <Icon name="check-circle" className="text-success" style={{ fontSize: "12px" }} />
                                      <span className="text-soft small text-truncate" style={{ maxWidth: "240px" }}>
                                        {item.url}
                                      </span>
                                    </div>
                                  ) : (
                                    <input
                                      type="url"
                                      className="form-control form-control-sm"
                                      placeholder="YouTube or direct video URL"
                                      value={item.url || ""}
                                      onChange={(e) => setEditClinic((prev) => {
                                        const arr = [...(prev.testimonialVideos || [])];
                                        arr[idx] = { ...arr[idx], url: e.target.value };
                                        return { ...prev, testimonialVideos: arr };
                                      })}
                                    />
                                  )}
                                </div>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  type="button"
                                  onClick={() => handleDeleteTestimonialVideo(idx)}
                                  aria-label={`Remove testimonial ${idx + 1}`}
                                >
                                  <Icon name="trash" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#f0f9ff" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="folder" className="me-2 text-info" />
                                <strong className="small">Choose from library</strong>
                              </div>
                              <p className="text-soft small mb-2">Pick testimonials you already added to your media library.</p>
                              <Button color="primary" size="sm" outline className="mt-auto" onClick={() => openMediaSelector("testimonials")}>
                                <Icon name="folder" className="me-1" /> Choose from library
                              </Button>
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#fefce8" }}>
                              <div className="d-flex align-items-center mb-1">
                                <Icon name="copy" className="me-2" style={{ color: "#ca8a04" }} />
                                <strong className="small">Use from another clinic</strong>
                              </div>
                              <p className="text-soft small mb-2">Copy from an existing clinic so you don&apos;t add them again.</p>
                              <Button color="light" size="sm" className="mt-auto" onClick={openCopyFromClinic}>
                                <Icon name="copy" className="me-1" /> Use from another clinic
                              </Button>
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* ── Bloc 4 : Photos ─────────────────────────────────── */}
                  <Col md="12">
                    <h6 className="overline-title">Photos</h6>
                    <p className="text-soft small mb-3">
                      Add photos that will appear on your page. You can upload new ones, pick from your library, or reuse photos from another clinic.
                    </p>
                  </Col>
                  <Col md="12">
                    <div className="row g-2 mb-3">
                      <Col md="4">
                        <div className="card card-bordered p-3 h-100" style={{ background: "#f8f9fa" }}>
                          <div className="d-flex align-items-center mb-1">
                            <Icon name="upload" className="me-2 text-primary" />
                            <strong className="small">Upload new</strong>
                          </div>
                          <p className="text-soft small mb-2">Add photos from your computer or phone.</p>
                    <FormGroup>
                      <label className="form-label">
                        Existing photos
                        <span className="text-soft ms-2">(These photos will appear in recommendations)</span>
                      </label>
                      
                      {/* Existing Photos */}
                      {existingPhotos.length > 0 && (
                        <div className="d-flex flex-wrap flex-g-2 mb-3">
                          {existingPhotos.map((photo, index) => (
                            <div key={index} style={{
                              position: 'relative',
                              width: '120px',
                              height: '120px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '2px solid #e5e7eb',
                              background: '#f9fafb'
                            }}>
                              <img
                                src={getClinicPhotoUrl(photo)}
                                alt={`${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/120?text=Photo';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleDeletePhoto(index)}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  border: 'none',
                                  background: 'rgba(239, 68, 68, 0.9)',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px'
                                }}
                                title="Delete photo"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* New Photos Preview */}
                      {newPhotoFiles.length > 0 && (
                        <div className="d-flex flex-wrap flex-g-2 mb-3">
                          {newPhotoFiles.map((file, index) => {
                            const preview = URL.createObjectURL(file);
                            return (
                              <div key={index} style={{
                                position: 'relative',
                                width: '120px',
                                height: '120px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '2px solid #10b981',
                                background: '#f0fdf4'
                              }}>
                                <img
                                  src={preview}
                                  alt={`${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <div style={{
                                  position: 'absolute',
                                  bottom: '0',
                                  left: '0',
                                  right: '0',
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  color: 'white',
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  padding: '4px',
                                  textAlign: 'center'
                                }}>
                                  NEW
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveNewPhoto(index)}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'rgba(239, 68, 68, 0.9)',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px'
                                  }}
                                  title="Remove from queue"
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Upload Button */}
                      <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoChange}
                          style={{ display: 'none' }}
                        />
                        <Icon name="image-add" className="me-1" />
                        Add photos
                      </label>
                      <p className="text-soft mt-2" style={{ fontSize: '12px' }}>
                        <Icon name="info" className="me-1" />
                        You can upload multiple photos at once. Max 5MB per photo.
                      </p>
                    </FormGroup>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#f0f9ff" }}>
                          <div className="d-flex align-items-center mb-1">
                            <Icon name="folder" className="me-2 text-info" />
                            <strong className="small">Choose from library</strong>
                          </div>
                          <p className="text-soft small mb-2">Pick photos you already added to your media library.</p>
                          <Button color="primary" size="sm" outline className="mt-auto" onClick={() => openMediaSelector("photos")}>
                            <Icon name="folder" className="me-1" /> Choose from library
                          </Button>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="card card-bordered p-3 h-100 d-flex flex-column" style={{ background: "#fefce8" }}>
                          <div className="d-flex align-items-center mb-1">
                            <Icon name="copy" className="me-2" style={{ color: "#ca8a04" }} />
                            <strong className="small">Use from another clinic</strong>
                          </div>
                          <p className="text-soft small mb-2">Copy the photos from an existing clinic so you don&apos;t add them again.</p>
                          <Button color="light" size="sm" className="mt-auto" onClick={openCopyFromClinic}>
                            <Icon name="copy" className="me-1" /> Use from another clinic
                          </Button>
                        </div>
                      </Col>
                    </div>
                  </Col>

                  <Col md="12" className="mt-2">
                    <p className="text-soft small mb-0">
                      <Icon name="info-fill" className="text-primary me-1" />
                      The <strong>Landing media</strong> blocks above (banner, before/after, testimonials, photos) are the only editors here
                      for <code>introVideoUrl</code>, <code>testimonialVideos</code>, and <code>beforeAfterCases</code> — same fields and API as the
                      partner clinic media page. A duplicate URL-only section was removed to avoid confusion.
                    </p>
                  </Col>
                </Form>
              </div>
        </AdminFormModal>

        {/* MediaSelector modal (Choose from library) */}
        <Modal isOpen={showMediaSelector} toggle={() => setShowMediaSelector(false)} className="modal-dialog-centered" size="lg">
          <ModalHeader toggle={() => setShowMediaSelector(false)}>
            {mediaSelectorModalTitle[pickerMode] || "Select from library"}
          </ModalHeader>
          <ModalBody>
            <MediaSelector
              multiple={pickerMode !== "banner"}
              usage="clinic"
              onSelect={handleMediaSelectorSelect}
              onCancel={() => setShowMediaSelector(false)}
              confirmButtonLabel={mediaSelectorConfirmLabel[pickerMode] || "Add selected"}
            />
          </ModalBody>
        </Modal>

        {/* Copy from another clinic modal */}
        <Modal isOpen={showCopyFromClinic} toggle={() => !copyFromClinicLoading && setShowCopyFromClinic(false)} className="modal-dialog-centered">
          <ModalHeader toggle={() => !copyFromClinicLoading && setShowCopyFromClinic(false)}>
            Use from another clinic
          </ModalHeader>
          <ModalBody>
            <p className="text-soft small mb-3">
              Copy media from an existing clinic so you don&apos;t add them again.
            </p>
            <FormGroup>
              <label className="form-label">Which clinic?</label>
              <RSelect
                options={clinicsForCopyDropdown}
                value={clinicsForCopyDropdown.find((o) => o.value === copyFromClinicId) || null}
                onChange={(opt) => setCopyFromClinicId(opt?.value ?? "")}
                placeholder="Select a clinic"
              />
            </FormGroup>
            <FormGroup>
              <label className="form-label">What do you want to copy?</label>
              <div className="d-flex flex-column flex-g-2">
                <label className="d-flex align-items-center flex-g-2 mb-0">
                  <input
                    type="checkbox"
                    checked={copyFromClinicTypes.banner}
                    onChange={(e) => setCopyFromClinicTypes((p) => ({ ...p, banner: e.target.checked }))}
                  />
                  Banner
                </label>
                <label className="d-flex align-items-center flex-g-2 mb-0">
                  <input
                    type="checkbox"
                    checked={copyFromClinicTypes.photos}
                    onChange={(e) => setCopyFromClinicTypes((p) => ({ ...p, photos: e.target.checked }))}
                  />
                  Photos
                </label>
                <label className="d-flex align-items-center flex-g-2 mb-0">
                  <input
                    type="checkbox"
                    checked={copyFromClinicTypes.beforeAfter}
                    onChange={(e) => setCopyFromClinicTypes((p) => ({ ...p, beforeAfter: e.target.checked }))}
                  />
                  Before &amp; after cases
                </label>
                <label className="d-flex align-items-center flex-g-2 mb-0">
                  <input
                    type="checkbox"
                    checked={copyFromClinicTypes.testimonials}
                    onChange={(e) => setCopyFromClinicTypes((p) => ({ ...p, testimonials: e.target.checked }))}
                  />
                  Testimonials
                </label>
              </div>
            </FormGroup>
            <div className="d-flex justify-content-end flex-g-2 mt-3">
              <Button color="light" onClick={() => setShowCopyFromClinic(false)} disabled={copyFromClinicLoading}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleCopyFromClinicSubmit} disabled={copyFromClinicLoading || !copyFromClinicId}>
                {copyFromClinicLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                Copy to this page
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Create clinic modal */}
        <AdminFormModal
          isOpen={modal.add}
          toggle={exitAddModal}
          title="Create clinic"
          subtitle="A public landing slug is generated automatically from the clinic name (unique on the platform) so the clinic can appear in patient-facing listings. Draft clinics stay off the catalogue until approved; prices, calendar, and practitioners are managed from the clinic record after creation."
          actions={
            formData.partnerId ? (
              <Button
                type="button"
                color="light"
                size="sm"
                outline
                onClick={() => history.push(getPublicUrl(`medical-partner-details/${formData.partnerId}`))}
              >
                Partner record
              </Button>
            ) : null
          }
          footer={
            <>
              <Button color="light" type="button" onClick={exitAddModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit" form="clinic-add-form" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating...
                  </>
                ) : (
                  "Create clinic"
                )}
              </Button>
            </>
          }
        >
              <div className="px-1">
                {loading && (
                  <div className="overlay-loading" style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(255,255,255,0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                  }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
                <Form id="clinic-add-form" className="row gy-4" onSubmit={handleSubmit(onCreateSubmit)}>
                  <Col md="12">
                    <h6 className="overline-title">Basic Information</h6>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Medical partner *</label>
                      <RSelect
                        options={partners.map(p => ({
                          value: p._id,
                          label: `${p.organization_name} (${p.contact_person_email || 'N/A'})`
                        }))}
                        value={partners.find(p => p._id === formData.partnerId) ? {
                          value: formData.partnerId,
                          label: partners.find(p => p._id === formData.partnerId)?.organization_name
                        } : null}
                        onChange={(e) => {
                          setFormData({ ...formData, partnerId: e.value });
                          setValue("partnerId", e.value);
                        }}
                        placeholder="Select a partner"
                      />
                      <input
                        type="hidden"
                        name="partnerId"
                        ref={register({ required: "Medical partner is required" })}
                        value={formData.partnerId}
                      />
                      {errors.partnerId && <span className="invalid-feedback d-block">{errors.partnerId.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Clinic name *</label>
                      <input
                        className="form-control"
                        type="text"
                        name="name"
                        defaultValue={formData.name}
                        placeholder="Enter clinic name"
                        ref={register({ required: "Clinic name is required" })}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                      {errors.name && <span className="invalid-feedback d-block">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        options={[
                          { value: "draft", label: "Draft" },
                          { value: "pending", label: "Pending" },
                          { value: "active", label: "Active" }
                        ]}
                        value={{ value: formData.status, label: formData.status === "draft" ? "Draft" : formData.status === "pending" ? "Pending" : "Active" }}
                        onChange={(e) => {
                          setFormData({ ...formData, status: e.value });
                          setValue("status", e.value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <h6 className="overline-title">Address</h6>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Street</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_street"
                        defaultValue={formData.address.street}
                        placeholder="Enter address"
                        ref={register}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, street: e.target.value } 
                        })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">City *</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_city"
                        defaultValue={formData.address.city}
                        placeholder="Enter city"
                        ref={register({ required: "City is required" })}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, city: e.target.value } 
                        })}
                      />
                      {errors.address_city && <span className="invalid-feedback d-block">{errors.address_city.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="add-clinic-country">
                        Country * (ISO)
                      </label>
                      <RSelect
                        inputId="add-clinic-country"
                        options={countries}
                        value={resolveIsoCountrySelectValue(formData.address.country) || null}
                        onChange={(opt) => {
                          const v = opt?.value ? String(opt.value).trim() : "";
                          setFormData((fd) => ({
                            ...fd,
                            address: { ...fd.address, country: v },
                          }));
                          setValue("address_country", v);
                        }}
                        placeholder="Select country…"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">State/Region</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_state"
                        defaultValue={formData.address.state}
                        placeholder="Enter state/region"
                        ref={register}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, state: e.target.value } 
                        })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Postal code</label>
                      <input
                        className="form-control"
                        type="text"
                        name="address_zipCode"
                        defaultValue={formData.address.zipCode}
                        placeholder="Enter postal code"
                        ref={register}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, zipCode: e.target.value } 
                        })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <h6 className="overline-title">Contact</h6>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        type="text"
                        name="phoneNumber"
                        defaultValue={formData.phoneNumber}
                        placeholder="Enter phone number"
                        ref={register}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Email</label>
                      <input
                        className="form-control"
                        type="email"
                        name="email"
                        defaultValue={formData.email}
                        placeholder="Enter email"
                        ref={register({
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                      {errors.email && <span className="invalid-feedback d-block">{errors.email.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Website</label>
                      <input
                        className="form-control"
                        type="url"
                        name="website"
                        defaultValue={formData.website}
                        placeholder="https://example.com"
                        ref={register}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Default currency</label>
                      <RSelect
                        options={[
                          { value: "USD", label: "USD - Dollar US" },
                          { value: "EUR", label: "EUR - Euro" },
                          { value: "GBP", label: "GBP - Livre Sterling" },
                          { value: "TRY", label: "TRY - Lire Turque" },
                          { value: "TND", label: "TND - Dinar Tunisien" },
                          { value: "MAD", label: "MAD - Dirham Marocain" }
                        ]}
                        value={{ value: formData.defaultCurrency, label: formData.defaultCurrency }}
                        onChange={(e) => {
                          setFormData({ ...formData, defaultCurrency: e.value });
                          setValue("defaultCurrency", e.value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="createAvailability"
                          checked={formData.availability}
                          onChange={(e) => {
                            setFormData({ ...formData, availability: e.target.checked });
                            setValue("availability", e.target.checked);
                          }}
                        />
                        <label className="custom-control-label" htmlFor="createAvailability">
                          Available
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="createIsDefault"
                          checked={formData.isDefault}
                          onChange={(e) => {
                            setFormData({ ...formData, isDefault: e.target.checked });
                            setValue("isDefault", e.target.checked);
                          }}
                        />
                        <label className="custom-control-label" htmlFor="createIsDefault">
                          Default clinic
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                </Form>
              </div>
        </AdminFormModal>

        <ConfirmModal
          isOpen={showDiscardConfirm}
          onClose={() => {
            setShowDiscardConfirm(false);
            setDiscardTarget(null);
          }}
          onConfirm={confirmDiscard}
          title="Discard unsaved changes?"
          message="You will lose edits made in this form."
          confirmText="Discard"
          cancelText="Keep editing"
          type="warning"
          loading={false}
        />

        <ConfirmModal
          isOpen={showConfirmModal.isOpen}
          onClose={() => setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null })}
          onConfirm={showConfirmModal.onConfirm || (() => {})}
          title={
            showConfirmModal.type === "approveClinic"
              ? "Approve clinic for the platform"
              : showConfirmModal.type === "rejectClinic"
              ? "Send clinic back to draft"
              : showConfirmModal.type === "deleteClinic"
              ? "Delete clinic permanently"
              : showConfirmModal.type === "suspendClinic"
              ? "Suspend clinic"
              : "Confirm"
          }
          message={
            showConfirmModal.type === "approveClinic"
              ? `Mark "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}" as active. Patient-facing visibility still follows teleconsultation flags, catalogue, and platform rules.`
              : showConfirmModal.type === "rejectClinic"
              ? `Return "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}" to draft so the partner can correct the file.`
              : showConfirmModal.type === "deleteClinic"
              ? `Permanently delete "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}"?\n\nThis cannot be undone (prices, media, linked rows).`
              : showConfirmModal.type === "suspendClinic"
              ? `Suspend "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}"?\n\nIt will be hidden from patients until you reactivate it.`
              : "Confirm this action?"
          }
          confirmText={
            showConfirmModal.type === "approveClinic"
              ? "Approve"
              : showConfirmModal.type === "rejectClinic"
              ? "Reject to draft"
              : showConfirmModal.type === "deleteClinic"
              ? "Delete"
              : showConfirmModal.type === "suspendClinic"
              ? "Suspend"
              : "Confirm"
          }
          cancelText="Cancel"
          type={
            showConfirmModal.type === "approveClinic"
              ? "success"
              : showConfirmModal.type === "rejectClinic"
              ? "danger"
              : showConfirmModal.type === "deleteClinic"
              ? "danger"
              : showConfirmModal.type === "suspendClinic"
              ? "warning"
              : "warning"
          }
          loading={loading}
        />
      </Content>
    </React.Fragment>
  );
};

export default ClinicManagement;




